import { Form } from 'antd';
import { DefaultRouteTabs } from '@/components/_common';
import { PageProps } from '@/types/PageProps';
import MovimentsOsXFinancialForm from '@/components/Reports/MovementReports/OsXFinancial/MovimentsOsXFinancialForm';
import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import {
  QuestReportOsXFinancialFormValues,
  QuestReportOsXFinancialRequest,
} from '@/core/domain/questReport/Reports/OsXFinancial/OsXFinancial.types';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { useReportGenerate } from '@/hooks';
import { useOsXFinancialReport } from '@/core/domain/questReport/Reports/OsXFinancial/OsXFinancial.hook';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { DateTimeFormat } from '@/utils/moment';
import { useOsXFinancialReportCsv } from '@/core/domain/questReport/CSV/OsXFinancial/OsXFinancial.hook';

const MovementOsXFinancialPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const [form] = Form.useForm<QuestReportOsXFinancialFormValues>();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();
  const report = useReportGenerate();

  const generateReport = useOsXFinancialReport();
  const generateReportCsv = useOsXFinancialReportCsv();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const {
        companiesIds,
        documentTypesIds,
        initialDate,
        finalDate,
        ...values
      } = await form.validateFields();

      const obj: QuestReportOsXFinancialRequest = {
        ...values,
        companiesIds: companiesIds?.toString(),
        employeesId: values.employeesId?.toString(),
        documentTypesIds: documentTypesIds?.toString(),
        startDate: initialDate.format(DateTimeFormat),
        endDate: finalDate.format(DateTimeFormat),
      };

      const generator = format === 'CSV' ? generateReportCsv : generateReport;
      const result = await generator.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const handleView = () => {
    handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: 'relatorio_os_x_financeiro.pdf',
        fileType: 'application/pdf',
      }),
    );
  };

  const handlePdf = () => {
    handleGenerateReport('PDF', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_os_x_financeiro.pdf',
      }),
    );
  };

  const handleCsv = () => {
    handleGenerateReport('CSV', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_os_x_financeiro.csv',
      }),
    );
  };

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <MovimentsOsXFinancialForm form={form} />

      <ButtonsToGenerateReports
        viewReport={handleView}
        generatePdf={handlePdf}
        generateCsv={handleCsv}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
};

export default MovementOsXFinancialPage;
